import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Search,
  BarChart3,
  Brain,
  AlertTriangle,
  LineChart,
  Lock,
  Star,
  Loader2,
  Mic,
  RefreshCw,
} from 'lucide-react';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { SignalBadge } from '../components/common/Badge';
import { StockLogo } from '../components/common/StockLogo';
import { InfoTooltip, TOOLTIPS } from '../components/common/InfoTooltip';
import { type ComparisonDataPoint } from '../components/charts/ComparisonChart';
import { useAuthStore, useMarketStore } from '../store';
import { useWatchlist } from '../hooks/useWatchlist';
import { getStockFast, getStockHistory, getUserLimits, getFullFundamentals, getStockPrices, getEarningsTranscripts, ensureEarningsTranscripts, analyzeStockWithProgress, isDataComplete, type HistoryDataPoint, type UserLimits, type FullFundamentalData, type StockPriceData } from '../utils/api';
import { formatCurrency, formatDateTime } from '../utils/format';
import { getSettingsPath } from '../utils/paths';
import { UpgradePrompt } from '../components/common/UpgradePrompt';
import { LockedFeature } from '../components/common/LockedFeature';
import { OverviewTab } from '../components/analysis/OverviewTab';
import { FundamentalsTab } from '../components/analysis/FundamentalsTab';
import { TechnicalsTab } from '../components/analysis/TechnicalsTab';
import { EarningsTab } from '../components/analysis/EarningsTab';
import type { EnhancedAnalysis, EarningsTranscriptsResponse } from '../types';

// Tier level mapping
const tierLevel: Record<string, number> = {
  'free': 0,
  'starter': 1,
  'early_supporter': 1,
  'pro': 2,
  'enterprise': 3
};

type AnalysisTab = 'overview' | 'fundamentals' | 'technicals' | 'earnings';

export function Analysis() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTicker = searchParams.get('ticker') || '';

  const [ticker, setTicker] = useState(initialTicker);
  const [searchInput, setSearchInput] = useState(initialTicker);
  const [analysis, setAnalysis] = useState<EnhancedAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryDataPoint[]>([]);
  const [historyDays, setHistoryDays] = useState(30);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [analysisLimitReached, setAnalysisLimitReached] = useState(false);
  const [userLimits, setUserLimits] = useState<UserLimits | null>(null);

  // Generation loading state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [limitError, setLimitError] = useState<{ limit: number; used: number } | null>(null);
  const [fundamentalsData, setFundamentalsData] = useState<FullFundamentalData | null>(null);
  const [loadingFundamentals, setLoadingFundamentals] = useState(false);
  const [additionalPriceData, setAdditionalPriceData] = useState<StockPriceData | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<AnalysisTab>(
    (searchParams.get('tab') as AnalysisTab) || 'overview'
  );

  // Earnings transcripts state
  const [transcriptsData, setTranscriptsData] = useState<EarningsTranscriptsResponse | null>(null);
  const [loadingTranscripts, setLoadingTranscripts] = useState(false);

  const { user } = useAuthStore();
  const { scannerData } = useMarketStore();
  const {
    isInWatchlist: checkInWatchlist,
    hasWatchlistAccess,
    toggleTicker,
  } = useWatchlist();

  // Check tier access
  const userTier = user?.subscription_tier || 'free';
  const userLevel = tierLevel[userTier] || 0;
  const isFreeUser = userLevel === 0;

  // For free users, only allow top 5 buy and top 5 sell tickers
  const allowedTickers = useMemo(() => {
    if (!isFreeUser) return null; // No restriction for paid users
    const buyTickers = (scannerData?.buy_opportunities || []).slice(0, 5).map(s => s.ticker);
    const sellTickers = (scannerData?.sell_signals || []).slice(0, 5).map(s => s.ticker);
    return new Set([...buyTickers, ...sellTickers]);
  }, [isFreeUser, scannerData]);

  const [tickerNotAllowed, setTickerNotAllowed] = useState(false);

  // Build comparison data from all sources (price, fundamentals)
  // Creates both annual and quarterly datasets
  const { annualComparisonData, quarterlyComparisonData } = useMemo(() => {
    if (!fundamentalsData) return { annualComparisonData: [], quarterlyComparisonData: [] };

    // Helper to get year and quarter from date string
    const getYear = (dateStr: string) => new Date(dateStr).getFullYear().toString();
    const getQuarter = (dateStr: string) => {
      const date = new Date(dateStr);
      const quarter = Math.ceil((date.getMonth() + 1) / 3);
      return `Q${quarter} ${date.getFullYear()}`;
    };

    // Build price lookup from weekly prices (sorted newest first)
    const weeklyPrices = fundamentalsData.weekly_prices || [];
    const findClosestPrice = (fiscalDate: string): number | undefined => {
      if (weeklyPrices.length === 0) return undefined;
      const targetDate = new Date(fiscalDate).getTime();

      // Find price closest to or before the fiscal date
      let closestPrice: number | undefined;
      let closestDiff = Infinity;

      for (const price of weeklyPrices) {
        const priceDate = new Date(price.date).getTime();
        const diff = Math.abs(targetDate - priceDate);
        // Only consider prices within 14 days of fiscal date
        if (diff < closestDiff && diff <= 14 * 24 * 60 * 60 * 1000) {
          closestDiff = diff;
          closestPrice = price.close;
        }
      }
      return closestPrice;
    };

    // Build ANNUAL data
    const annualByPeriod = new Map<string, ComparisonDataPoint>();

    const ensureAnnual = (year: string, fiscalDate: string) => {
      if (!annualByPeriod.has(year)) {
        annualByPeriod.set(year, { period: year, fiscal_date: fiscalDate });
      }
    };

    // Add annual earnings (EPS)
    fundamentalsData.earnings?.annual_earnings?.forEach(item => {
      if (item.fiscal_date) {
        const year = getYear(item.fiscal_date);
        ensureAnnual(year, item.fiscal_date);
        annualByPeriod.get(year)!.eps = item.reported_eps;
      }
    });

    // Add annual income statement
    fundamentalsData.income_statement?.annual_reports?.forEach(item => {
      if (item.fiscal_date) {
        const year = getYear(item.fiscal_date);
        ensureAnnual(year, item.fiscal_date);
        const entry = annualByPeriod.get(year)!;
        entry.revenue = item.total_revenue;
        entry.net_income = item.net_income;
        entry.gross_profit = item.gross_profit;
      }
    });

    // Add annual cash flow
    fundamentalsData.cash_flow?.annual_reports?.forEach(item => {
      if (item.fiscal_date) {
        const year = getYear(item.fiscal_date);
        ensureAnnual(year, item.fiscal_date);
        const entry = annualByPeriod.get(year)!;
        entry.free_cash_flow = item.free_cash_flow;
        entry.operating_cashflow = item.operating_cashflow;
        entry.buybacks = item.stock_repurchase;
        entry.sbc = item.stock_based_compensation;
      }
    });

    // Add annual balance sheet
    fundamentalsData.balance_sheet?.annual_reports?.forEach(item => {
      if (item.fiscal_date) {
        const year = getYear(item.fiscal_date);
        ensureAnnual(year, item.fiscal_date);
        annualByPeriod.get(year)!.shares_outstanding = item.common_stock_shares_outstanding;
      }
    });

    // Calculate FCF per share and add stock price for annual
    annualByPeriod.forEach(entry => {
      if (entry.free_cash_flow && entry.shares_outstanding) {
        entry.fcf_per_share = entry.free_cash_flow / entry.shares_outstanding;
      }
      // Add stock price from weekly data
      entry.stock_price = findClosestPrice(entry.fiscal_date);
    });

    // Build QUARTERLY data
    const quarterlyByPeriod = new Map<string, ComparisonDataPoint>();

    const ensureQuarterly = (period: string, fiscalDate: string) => {
      if (!quarterlyByPeriod.has(period)) {
        quarterlyByPeriod.set(period, { period, fiscal_date: fiscalDate });
      }
    };

    // Add quarterly earnings (EPS)
    fundamentalsData.earnings?.quarterly_earnings?.forEach(item => {
      if (item.fiscal_date) {
        const period = getQuarter(item.fiscal_date);
        ensureQuarterly(period, item.fiscal_date);
        quarterlyByPeriod.get(period)!.eps = item.reported_eps;
      }
    });

    // Add quarterly income statement
    fundamentalsData.income_statement?.quarterly_reports?.forEach(item => {
      if (item.fiscal_date) {
        const period = getQuarter(item.fiscal_date);
        ensureQuarterly(period, item.fiscal_date);
        const entry = quarterlyByPeriod.get(period)!;
        entry.revenue = item.total_revenue;
        entry.net_income = item.net_income;
        entry.gross_profit = item.gross_profit;
      }
    });

    // Add quarterly cash flow
    fundamentalsData.cash_flow?.quarterly_reports?.forEach(item => {
      if (item.fiscal_date) {
        const period = getQuarter(item.fiscal_date);
        ensureQuarterly(period, item.fiscal_date);
        const entry = quarterlyByPeriod.get(period)!;
        entry.free_cash_flow = item.free_cash_flow;
        entry.operating_cashflow = item.operating_cashflow;
        entry.buybacks = item.stock_repurchase;
        entry.sbc = item.stock_based_compensation;
      }
    });

    // Add quarterly balance sheet
    fundamentalsData.balance_sheet?.quarterly_reports?.forEach(item => {
      if (item.fiscal_date) {
        const period = getQuarter(item.fiscal_date);
        ensureQuarterly(period, item.fiscal_date);
        quarterlyByPeriod.get(period)!.shares_outstanding = item.common_stock_shares_outstanding;
      }
    });

    // Calculate FCF per share and add stock price for quarterly
    quarterlyByPeriod.forEach(entry => {
      if (entry.free_cash_flow && entry.shares_outstanding) {
        entry.fcf_per_share = entry.free_cash_flow / entry.shares_outstanding;
      }
      // Add stock price from weekly data
      entry.stock_price = findClosestPrice(entry.fiscal_date);
    });

    // Convert to arrays and sort by date
    const annualData = Array.from(annualByPeriod.values())
      .sort((a, b) => new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime());

    const quarterlyData = Array.from(quarterlyByPeriod.values())
      .sort((a, b) => new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime());

    return { annualComparisonData: annualData, quarterlyComparisonData: quarterlyData };
  }, [fundamentalsData]);

  // Fetch user limits on mount for all users (to show daily usage)
  useEffect(() => {
    getUserLimits()
      .then(setUserLimits)
      .catch(console.warn);
  }, []);

  const fetchAnalysis = async (symbol: string) => {
    if (!symbol) return;

    // Check if free user is trying to access a non-allowed ticker
    if (isFreeUser && allowedTickers && !allowedTickers.has(symbol.toUpperCase())) {
      setTickerNotAllowed(true);
      setError(null);
      setAnalysis(null);
      setTicker(symbol);
      setSearchParams({ ticker: symbol });
      return;
    }

    setTickerNotAllowed(false);

    try {
      setLoading(true);
      setError(null);
      setLimitError(null);
      setAnalysisLimitReached(false);
      setTranscriptsData(null); // Reset transcripts for new ticker

      // Start history, fundamentals, and transcripts fetches immediately (don't wait for analysis)
      fetchHistory(symbol, historyDays);
      fetchFundamentals(symbol);
      fetchTranscripts(symbol);

      // Try FAST cached data first (instant)
      let data: EnhancedAnalysis | null = null;
      try {
        data = await getStockFast(symbol);

        // Check if data is complete (has actual scores, not zeros)
        if (data && isDataComplete(data)) {
          setAnalysis(data);
          setTicker(symbol);
          setSearchParams({ ticker: symbol });

          // If price cache is insufficient, fetch more in background
          if (data.price_cache_sufficient === false) {
            fetchAdditionalPrices(symbol);
          }

          // Refresh user limits to show updated usage
          if (isFreeUser || userLimits?.limits?.max_analyses_per_day !== null) {
            getUserLimits().then(setUserLimits).catch(console.warn);
          }
          return;
        }

        // Data incomplete - need to generate fresh analysis
        throw new Error('incomplete_data');

      } catch (fastError) {
        // Show generation loading state
        setLoading(false);  // Hide main loader
        setIsGenerating(true);
        setGenerationProgress('Generating sentiment analysis...');

        try {
          // Call analyze endpoint with progress updates
          setGenerationProgress('Analyzing market sentiment...');
          data = await analyzeStockWithProgress(symbol, (progress) => {
            setGenerationProgress(progress);
          });

          setAnalysis(data);
          setTicker(symbol);
          setSearchParams({ ticker: symbol });

          // If price cache is insufficient, fetch more in background
          if (data?.price_cache_sufficient === false) {
            fetchAdditionalPrices(symbol);
          }

          // Refresh user limits to show updated usage
          getUserLimits().then(setUserLimits).catch(console.warn);

        } catch (analyzeError: unknown) {
          console.error('Analysis generation failed:', analyzeError);

          // Check if it's a daily limit exceeded error
          if (analyzeError && typeof analyzeError === 'object' && 'response' in analyzeError) {
            const response = (analyzeError as { response?: { data?: { error?: string; limit?: number; used?: number } } }).response;
            if (response?.data?.error === 'daily_analysis_limit_exceeded') {
              setLimitError({
                limit: response.data.limit || 3,
                used: response.data.used || 3
              });
              setAnalysisLimitReached(true);
              setError('Daily analysis limit reached');
              return;
            }
            // Also check for monthly limit error (backwards compatibility)
            if (response?.data?.error === 'analysis_limit_exceeded') {
              setAnalysisLimitReached(true);
              setError('You have reached your analysis limit.');
              return;
            }
          }

          setError(`Failed to analyze ${symbol}. Please check the ticker symbol.`);
        } finally {
          setIsGenerating(false);
        }
      }
    } catch (err: unknown) {
      console.error('Analysis failed:', err);
      setError(`Failed to analyze ${symbol}. Please check the ticker symbol.`);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (symbol: string, days: number) => {
    try {
      setLoadingHistory(true);
      const response = await getStockHistory(symbol, days);
      setHistoryData(response.history);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setHistoryData([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchAdditionalPrices = async (symbol: string) => {
    try {
      const result = await getStockPrices(symbol);
      setAdditionalPriceData(result);
    } catch (err) {
      console.error('Failed to fetch additional prices:', err);
    }
  };

  const fetchFundamentals = async (symbol: string) => {
    try {
      setLoadingFundamentals(true);
      const result = await getFullFundamentals(symbol);
      setFundamentalsData(result);
    } catch (err) {
      console.error('Failed to fetch fundamentals:', err);
      setFundamentalsData(null);
    } finally {
      setLoadingFundamentals(false);
    }
  };

  const fetchTranscripts = async (symbol: string) => {
    try {
      setLoadingTranscripts(true);
      const data = await getEarningsTranscripts(symbol, 8);
      setTranscriptsData(data);

      // If no transcripts have summaries, try to ensure they exist
      const hasSummaries = data.transcripts?.some(t => t.ai_summary);
      if (!hasSummaries) {
        try {
          await ensureEarningsTranscripts(symbol);
          // Re-fetch after ensuring
          const refreshed = await getEarningsTranscripts(symbol, 8);
          setTranscriptsData(refreshed);
        } catch {
          // Silently fail - user still sees whatever we have
        }
      }
    } catch (err) {
      console.error('Failed to fetch transcripts:', err);
    } finally {
      setLoadingTranscripts(false);
    }
  };

  const handleTimeRangeChange = (days: number) => {
    setHistoryDays(days);
    if (ticker) {
      fetchHistory(ticker, days);
    }
  };

  const handleTabChange = (tab: AnalysisTab) => {
    setActiveTab(tab);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (tab === 'overview') {
        params.delete('tab');
      } else {
        params.set('tab', tab);
      }
      return params;
    });
  };

  // Lazy-load earnings transcripts when tab is selected
  useEffect(() => {
    if (activeTab === 'earnings' && ticker && !transcriptsData && !loadingTranscripts) {
      setLoadingTranscripts(true);
      getEarningsTranscripts(ticker)
        .then(setTranscriptsData)
        .catch(err => console.error('Failed to fetch transcripts:', err))
        .finally(() => setLoadingTranscripts(false));
    }
  }, [activeTab, ticker]);

  useEffect(() => {
    if (initialTicker) {
      fetchAnalysis(initialTicker);
    }
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      fetchAnalysis(searchInput.trim().toUpperCase());
    }
  };

  // Check if stock is in watchlist
  const isInWatchlist = ticker ? checkInWatchlist(ticker) : false;
  const [togglingWatchlist, setTogglingWatchlist] = useState(false);

  const handleToggleWatchlist = async () => {
    if (!hasWatchlistAccess) {
      navigate(getSettingsPath('subscription'));
      return;
    }
    if (ticker && !togglingWatchlist) {
      setTogglingWatchlist(true);
      await toggleTicker(ticker);
      setTogglingWatchlist(false);
    }
  };

  // Calculate remaining analyses for users with daily limits
  const analysesTodayUsed = userLimits?.usage?.analyses_today || 0;
  const maxDailyAnalyses = userLimits?.limits?.max_analyses_per_day;
  const hasDailyLimit = maxDailyAnalyses !== null && maxDailyAnalyses !== undefined;
  const analysesRemaining = hasDailyLimit ? Math.max(0, maxDailyAnalyses - analysesTodayUsed) : null;

  return (
    <div className="space-y-4">
      {/* Analysis limit reached prompt */}
      {analysisLimitReached && !limitError && (
        <UpgradePrompt
          currentTier={userTier}
          limitType={`${maxDailyAnalyses || 3} daily analysis`}
          proValue="unlimited"
        />
      )}

      {/* Header with Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">
              Stock Analysis
            </h1>
            {/* Daily usage indicator for users with limits */}
            {hasDailyLimit && !analysisLimitReached && analysesRemaining !== null && (
              <span
                className={clsx(
                  'text-xs px-2 py-1 rounded-full',
                  analysesRemaining <= 1
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)]'
                )}
                title="Daily analysis limit"
              >
                {analysesRemaining}/{maxDailyAnalyses} today
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Find the gap between sentiment and fundamentals - LOW sentiment + STRONG fundamentals = opportunity
          </p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-muted)]" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
              placeholder="Enter ticker..."
              className={clsx(
                'w-full h-10 pl-10 pr-4 rounded-lg',
                'bg-[var(--color-bg-primary)] border border-[var(--color-border)]',
                'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]',
                'focus:outline-none focus:ring-2 focus:ring-blue-500/50'
              )}
            />
          </div>
          <Button type="submit" loading={loading}>
            Analyze
          </Button>
        </form>
      </div>

      {/* Free tier - show empty state if no ticker */}
      {isFreeUser && !ticker && (
        <Card className="text-center py-16">
          <Search className="h-16 w-16 text-[var(--color-text-muted)] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
            Enter a Ticker Symbol
          </h3>
          <p className="text-[var(--color-text-muted)] mt-2 max-w-md mx-auto">
            Search for any stock to preview what our analysis includes.
            Upgrade to Starter to unlock full analysis for any stock.
          </p>
        </Card>
      )}

      {/* Free tier - show locked analysis page when ticker is entered */}
      {isFreeUser && ticker && (
        <LockedFeature
          feature="Stock analysis with AI-powered insights, sentiment scores, and fundamental analysis. Upgrade to analyze any stock."
          requiredTier="starter"
        >
          {/* Show a preview of what they would see */}
          <Card>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <StockLogo ticker={ticker} name={ticker} size="xl" className="w-16 h-16" />
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-[var(--color-text-primary)]">
                      {ticker}
                    </h2>
                    <SignalBadge signal="HOLD" size="md" />
                  </div>
                  <p className="text-[var(--color-text-muted)]">Loading analysis...</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-[var(--color-text-muted)]">Current Price</p>
                  <p className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">$---</p>
                </div>
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-[var(--color-text-muted)]">Disconnect</p>
                  <p className="text-xl sm:text-2xl font-bold font-mono text-emerald-500">--</p>
                </div>
              </div>
            </div>
          </Card>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
            <Card className="h-48"><div className="h-full bg-[var(--color-bg-secondary)] rounded animate-pulse" /></Card>
            <Card className="h-48"><div className="h-full bg-[var(--color-bg-secondary)] rounded animate-pulse" /></Card>
            <Card className="h-48"><div className="h-full bg-[var(--color-bg-secondary)] rounded animate-pulse" /></Card>
          </div>
          <Card className="mt-6 h-64"><div className="h-full bg-[var(--color-bg-secondary)] rounded animate-pulse" /></Card>
        </LockedFeature>
      )}

      {/* Paid users - show full content */}
      {!isFreeUser && tickerNotAllowed && (
        <Card className="text-center py-16">
          <Lock className="h-16 w-16 text-[var(--color-text-muted)] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
            {ticker} Analysis Locked
          </h3>
          <p className="text-[var(--color-text-muted)] mt-2 max-w-md mx-auto">
            This stock is not available on your current plan.
            Upgrade to access analysis for any stock.
          </p>
          <div className="mt-6">
            <UpgradePrompt
              currentTier="free"
              limitType="top 10 stocks"
              proValue="unlimited stocks"
              compact
            />
          </div>
        </Card>
      )}

      {/* Content - only for paid users */}
      {!isFreeUser && !tickerNotAllowed && loading ? (
        null
      ) : !isFreeUser && !tickerNotAllowed && isGenerating ? (
        <Card className="text-center py-16">
          <RefreshCw className="h-12 w-12 text-[var(--color-accent)] mx-auto mb-4 animate-spin" />
          <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
            Generating Analysis
          </h3>
          <p className="text-[var(--color-text-muted)] mt-2">{generationProgress}</p>
          <p className="text-sm text-[var(--color-text-muted)] mt-4">
            This may take 15-30 seconds...
          </p>
        </Card>
      ) : !isFreeUser && !tickerNotAllowed && limitError ? (
        <Card className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
            Daily Limit Reached
          </h3>
          <p className="text-[var(--color-text-muted)] mt-2">
            You've used {limitError.used} of {limitError.limit} daily analyses.
          </p>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Your limit resets at midnight UTC.
          </p>
          <Button
            className="mt-6"
            onClick={() => navigate(getSettingsPath('subscription'))}
          >
            Upgrade for More
          </Button>
        </Card>
      ) : !isFreeUser && !tickerNotAllowed && error ? (
        <Card className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
            Analysis Failed
          </h3>
          <p className="text-[var(--color-text-muted)] mt-2">{error}</p>
        </Card>
      ) : !isFreeUser && !tickerNotAllowed && !analysis ? (
        <Card className="text-center py-16">
          <Search className="h-16 w-16 text-[var(--color-text-muted)] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
            Enter a Ticker Symbol
          </h3>
          <p className="text-[var(--color-text-muted)] mt-2 max-w-md mx-auto">
            Search for any stock to get comprehensive sentiment analysis, fundamental metrics, and
            AI-powered insights.
          </p>
        </Card>
      ) : !isFreeUser && !tickerNotAllowed && analysis ? (
        <>
          {/* Stock Header */}
          <Card>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <StockLogo ticker={analysis.ticker} name={analysis.name} size="xl" className="w-16 h-16" />
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-[var(--color-text-primary)]">
                      {analysis.ticker}
                    </h2>
                    <SignalBadge signal={analysis.disconnect?.signal || 'HOLD'} size="md" />
                  </div>
                  <p className="text-[var(--color-text-muted)]">{analysis.name}</p>
                  {analysis.timestamp && (
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                      Updated {formatDateTime(analysis.timestamp)}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <p className="text-xs sm:text-sm text-[var(--color-text-muted)]">Current Price</p>
                    <InfoTooltip content={TOOLTIPS.currentPrice} position="bottom" />
                  </div>
                  <p className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">
                    {formatCurrency(analysis.technical?.current_price || 0)}
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <p className="text-xs sm:text-sm text-[var(--color-text-muted)]">Disconnect</p>
                    <InfoTooltip content={TOOLTIPS.disconnectScore} position="bottom" />
                  </div>
                  <p
                    className={clsx(
                      'text-xl sm:text-2xl font-bold font-mono',
                      (analysis.disconnect?.disconnect_score || 0) > 50
                        ? 'text-emerald-500'
                        : 'text-red-500'
                    )}
                  >
                    {(analysis.disconnect?.disconnect_score || 0).toFixed(0)}
                  </p>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={handleToggleWatchlist}
                    disabled={togglingWatchlist}
                    className={clsx(
                      'p-2 rounded-lg transition-all',
                      hasWatchlistAccess
                        ? isInWatchlist
                          ? 'text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10'
                          : 'text-[var(--color-text-muted)] hover:text-yellow-500 hover:bg-[var(--color-bg-secondary)]'
                        : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-secondary)]',
                      togglingWatchlist && 'opacity-50 cursor-wait'
                    )}
                    title={
                      !hasWatchlistAccess
                        ? 'Upgrade to add to watchlist'
                        : isInWatchlist
                        ? 'Remove from watchlist'
                        : 'Add to watchlist'
                    }
                  >
                    {togglingWatchlist ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : !hasWatchlistAccess ? (
                      <Lock className="h-5 w-5" />
                    ) : (
                      <Star
                        className={clsx('h-5 w-5', isInWatchlist && 'fill-current')}
                      />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Card>

          {/* Tab Bar */}
          <div className="flex items-center gap-1 p-1 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
            {([
              { key: 'overview' as AnalysisTab, label: 'Overview', icon: Brain },
              { key: 'fundamentals' as AnalysisTab, label: 'Fundamentals', icon: BarChart3 },
              { key: 'technicals' as AnalysisTab, label: 'Technicals', icon: LineChart },
              { key: 'earnings' as AnalysisTab, label: 'Earnings', icon: Mic },
            ]).map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => handleTabChange(key)}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all flex-1 justify-center',
                  activeTab === key
                    ? 'bg-[var(--color-accent)] text-white shadow-sm'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)]'
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <OverviewTab
              analysis={analysis}
              additionalPriceData={additionalPriceData}
              transcriptsData={transcriptsData}
              loadingTranscripts={loadingTranscripts}
              onViewEarnings={() => handleTabChange('earnings')}
            />
          )}

          {activeTab === 'fundamentals' && (
            <FundamentalsTab
              fundamentalsData={fundamentalsData}
              loadingFundamentals={loadingFundamentals}
              annualComparisonData={annualComparisonData}
              quarterlyComparisonData={quarterlyComparisonData}
              ticker={ticker}
            />
          )}

          {activeTab === 'technicals' && (
            <TechnicalsTab
              analysis={analysis}
              additionalPriceData={additionalPriceData}
              historyData={historyData}
              historyDays={historyDays}
              loadingHistory={loadingHistory}
              onTimeRangeChange={handleTimeRangeChange}
            />
          )}

          {activeTab === 'earnings' && (
            <EarningsTab
              transcriptsData={transcriptsData}
              loading={loadingTranscripts}
            />
          )}
        </>
      ) : null}
    </div>
  );
}
